import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './Admin.css';
import './AdminSignatures.css';

function AdminSignatures() {
    const [signatures, setSignatures] = useState([]);
    const [configs, setConfigs] = useState([]);
    const [hospitalStamp, setHospitalStamp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [activeTab, setActiveTab] = useState('config');
    const [editingConfig, setEditingConfig] = useState(null);
    const [editingStampConfig, setEditingStampConfig] = useState(null);
    const stampInputRef = useRef(null);

    const documentTypes = [
        { id: 'salary_slip', name: 'สลิปเงินเดือน', description: 'ลายเซ็นในสลิปเงินเดือน' },
        { id: '50_tawi', name: 'หนังสือ 50 ทวิ', description: 'ลายเซ็นในหนังสือรับรองภาษีหัก ณ ที่จ่าย' }
    ];

    const fetchSignatures = useCallback(async () => {
        try {
            const response = await api.get('/signatures/all');
            setSignatures(response.data);
        } catch (err) {
            console.error('Error fetching signatures:', err);
        }
    }, []);

    const fetchConfigs = useCallback(async () => {
        try {
            const response = await api.get('/signatures/stamp-config');
            setConfigs(response.data);
        } catch (err) {
            console.error('Error fetching configs:', err);
        }
    }, []);

    const fetchHospitalStamp = useCallback(async () => {
        try {
            const response = await api.get('/signatures/hospital-stamp');
            setHospitalStamp(response.data);
        } catch (err) {
            console.error('Error fetching hospital stamp:', err);
        }
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchSignatures(), fetchConfigs(), fetchHospitalStamp()]);
            setLoading(false);
        };
        loadData();
    }, [fetchSignatures, fetchConfigs, fetchHospitalStamp]);

    // Handle hospital stamp upload
    const handleStampUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('stamp', file);
        formData.append('stamp_name', 'ตราประทับโรงพยาบาลศรีสะเกษ');

        try {
            setUploading(true);
            await api.post('/signatures/hospital-stamp', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setMessage({ type: 'success', text: 'อัพโหลดตราประทับสำเร็จ' });
            await fetchHospitalStamp();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาดในการอัพโหลด' });
        } finally {
            setUploading(false);
            if (stampInputRef.current) {
                stampInputRef.current.value = '';
            }
        }
    };

    // Delete hospital stamp
    const handleDeleteStamp = async () => {
        if (!window.confirm('ต้องการลบตราประทับนี้หรือไม่?')) return;

        try {
            await api.delete('/signatures/hospital-stamp');
            setMessage({ type: 'success', text: 'ลบตราประทับสำเร็จ' });
            setHospitalStamp(null);
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    // Handle stamp config edit
    const handleEditStampConfig = (docType) => {
        const config = getConfigForType(docType);
        setEditingStampConfig({
            document_type: docType,
            stamp_enabled: config.stamp_enabled || false,
            stamp_position: config.stamp_position || 'below',
            stamp_offset_x: config.stamp_offset_x || 0,
            stamp_offset_y: config.stamp_offset_y || 0,
            stamp_width: config.stamp_width || 80,
            stamp_height: config.stamp_height || 80
        });
    };

    // Save stamp config
    const handleSaveStampConfig = async () => {
        if (!editingStampConfig) return;

        try {
            setSaving(true);
            await api.put(`/signatures/stamp-config/${editingStampConfig.document_type}`, {
                stamp_enabled: editingStampConfig.stamp_enabled,
                stamp_position: editingStampConfig.stamp_position,
                stamp_offset_x: parseInt(editingStampConfig.stamp_offset_x),
                stamp_offset_y: parseInt(editingStampConfig.stamp_offset_y),
                stamp_width: parseInt(editingStampConfig.stamp_width),
                stamp_height: parseInt(editingStampConfig.stamp_height)
            });

            setMessage({ type: 'success', text: 'บันทึกการตั้งค่าตราประทับสำเร็จ' });
            await fetchConfigs();
            setEditingStampConfig(null);
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSaving(false);
        }
    };

    const getConfigForType = (docType) => {
        return configs.find(c => c.document_type === docType) || {
            document_type: docType,
            signature_id: null,
            position_x: 150,
            position_y: 0,
            width: 156,
            height: 78,
            is_active: true
        };
    };

    const handleEditConfig = (docType) => {
        const config = getConfigForType(docType);
        setEditingConfig({ ...config });
    };

    const handleSaveConfig = async () => {
        if (!editingConfig) return;

        try {
            setSaving(true);
            await api.put(`/signatures/config/${editingConfig.document_type}`, {
                signature_id: editingConfig.signature_id,
                position_x: parseInt(editingConfig.position_x),
                position_y: parseInt(editingConfig.position_y),
                width: parseInt(editingConfig.width),
                height: parseInt(editingConfig.height),
                is_active: editingConfig.is_active
            });

            setMessage({ type: 'success', text: 'บันทึกการตั้งค่าสำเร็จ' });
            await fetchConfigs();
            setEditingConfig(null);
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSaving(false);
        }
    };

    const getSignatureOwner = (sigId) => {
        const sig = signatures.find(s => s.id === sigId);
        return sig ? sig.owner_name : 'ไม่ได้เลือก';
    };

    if (loading) {
        return (
            <div className="flex-center" style={{ padding: '60px 0' }}>
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            {/* Breadcrumb */}
            <div className="breadcrumb">
                <Link to="/admin">จัดการระบบ</Link>
                <span>/</span>
                <span>ตั้งค่าลายเซ็นเอกสาร</span>
            </div>

            <div className="admin-header">
                <h1>ตั้งค่าลายเซ็นเอกสาร</h1>
                <p>กำหนดลายเซ็นและตำแหน่งสำหรับแต่ละประเภทเอกสาร</p>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    {message.text}
                </div>
            )}

            {/* Tabs */}
            <div className="admin-tabs">
                <button
                    className={`admin-tab ${activeTab === 'config' ? 'active' : ''}`}
                    onClick={() => setActiveTab('config')}
                >
                    ตั้งค่าลายเซ็น
                </button>
                <button
                    className={`admin-tab ${activeTab === 'stamp' ? 'active' : ''}`}
                    onClick={() => setActiveTab('stamp')}
                >
                    ตราประทับ
                </button>
                <button
                    className={`admin-tab ${activeTab === 'signatures' ? 'active' : ''}`}
                    onClick={() => setActiveTab('signatures')}
                >
                    ลายเซ็นทั้งหมด ({signatures.filter(s => s.is_active).length})
                </button>
            </div>

            {activeTab === 'config' && (
                <div className="config-grid">
                    {documentTypes.map(docType => {
                        const config = getConfigForType(docType.id);
                        const isEditing = editingConfig?.document_type === docType.id;

                        return (
                            <div key={docType.id} className="steam-card config-card">
                                <div className="steam-card-header flex-between">
                                    <span>{docType.name}</span>
                                    {!isEditing && (
                                        <button
                                            className="steam-btn steam-btn-secondary steam-btn-sm"
                                            onClick={() => handleEditConfig(docType.id)}
                                        >
                                            แก้ไข
                                        </button>
                                    )}
                                </div>
                                <div className="steam-card-body">
                                    {isEditing ? (
                                        <div className="config-form">
                                            <div className="form-group">
                                                <label className="form-label">เลือกลายเซ็น</label>
                                                <select
                                                    className="steam-input"
                                                    value={editingConfig.signature_id || ''}
                                                    onChange={(e) => setEditingConfig({
                                                        ...editingConfig,
                                                        signature_id: e.target.value ? parseInt(e.target.value) : null
                                                    })}
                                                >
                                                    <option value="">-- ไม่ใช้ลายเซ็น --</option>
                                                    {signatures.filter(s => s.is_active).map(sig => (
                                                        <option key={sig.id} value={sig.id}>
                                                            {sig.owner_name} ({sig.emp_code || 'N/A'})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label className="form-label">ตำแหน่ง X</label>
                                                    <input
                                                        type="number"
                                                        className="steam-input"
                                                        value={editingConfig.position_x}
                                                        onChange={(e) => setEditingConfig({
                                                            ...editingConfig,
                                                            position_x: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label className="form-label">ตำแหน่ง Y</label>
                                                    <input
                                                        type="number"
                                                        className="steam-input"
                                                        value={editingConfig.position_y}
                                                        onChange={(e) => setEditingConfig({
                                                            ...editingConfig,
                                                            position_y: e.target.value
                                                        })}
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label className="form-label">ความกว้าง</label>
                                                    <input
                                                        type="number"
                                                        className="steam-input"
                                                        value={editingConfig.width}
                                                        onChange={(e) => setEditingConfig({
                                                            ...editingConfig,
                                                            width: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label className="form-label">ความสูง</label>
                                                    <input
                                                        type="number"
                                                        className="steam-input"
                                                        value={editingConfig.height}
                                                        onChange={(e) => setEditingConfig({
                                                            ...editingConfig,
                                                            height: e.target.value
                                                        })}
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-group">
                                                <label className="checkbox-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={editingConfig.is_active}
                                                        onChange={(e) => setEditingConfig({
                                                            ...editingConfig,
                                                            is_active: e.target.checked
                                                        })}
                                                    />
                                                    <span>เปิดใช้งานลายเซ็นในเอกสารนี้</span>
                                                </label>
                                            </div>

                                            <div className="form-actions">
                                                <button
                                                    className="steam-btn steam-btn-secondary"
                                                    onClick={() => setEditingConfig(null)}
                                                >
                                                    ยกเลิก
                                                </button>
                                                <button
                                                    className="steam-btn steam-btn-primary"
                                                    onClick={handleSaveConfig}
                                                    disabled={saving}
                                                >
                                                    {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="config-info">
                                            <div className="info-item">
                                                <span className="label">ผู้ลงนาม:</span>
                                                <span className="value">{config.signature_id ? getSignatureOwner(config.signature_id) : 'ไม่ได้เลือก'}</span>
                                            </div>
                                            <div className="info-item">
                                                <span className="label">ตำแหน่ง:</span>
                                                <span className="value">X: {config.position_x}, Y: {config.position_y}</span>
                                            </div>
                                            <div className="info-item">
                                                <span className="label">ขนาด:</span>
                                                <span className="value">{config.width} x {config.height} px</span>
                                            </div>
                                            <div className="info-item">
                                                <span className="label">สถานะ:</span>
                                                <span className={`status-badge ${config.is_active ? 'active' : 'inactive'}`}>
                                                    {config.is_active ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                                                </span>
                                            </div>
                                            {config.signature_url && (
                                                <div className="signature-preview-small">
                                                    <img src={`/api${config.signature_url}`} alt="ลายเซ็น" />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {activeTab === 'stamp' && (
                <div className="stamp-section">
                    {/* Hospital Stamp Upload Card */}
                    <div className="steam-card">
                        <div className="steam-card-header">
                            <span>ตราประทับโรงพยาบาล</span>
                        </div>
                        <div className="steam-card-body">
                            {hospitalStamp ? (
                                <div className="stamp-current">
                                    <div className="stamp-preview">
                                        <img
                                            src={`/api${hospitalStamp.file_url}`}
                                            alt="ตราประทับ"
                                        />
                                    </div>
                                    <div className="stamp-info">
                                        <p><strong>{hospitalStamp.stamp_name}</strong></p>
                                        <small>อัพโหลดเมื่อ: {new Date(hospitalStamp.created_at).toLocaleDateString('th-TH')}</small>
                                    </div>
                                    <div className="stamp-actions">
                                        <button
                                            className="steam-btn steam-btn-secondary"
                                            onClick={() => stampInputRef.current?.click()}
                                            disabled={uploading}
                                        >
                                            เปลี่ยนตราประทับ
                                        </button>
                                        <button
                                            className="steam-btn steam-btn-danger"
                                            onClick={handleDeleteStamp}
                                        >
                                            ลบ
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="stamp-upload-area" onClick={() => stampInputRef.current?.click()}>
                                    <div className="upload-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="17 8 12 3 7 8" />
                                            <line x1="12" y1="3" x2="12" y2="15" />
                                        </svg>
                                    </div>
                                    <p>คลิกเพื่ออัพโหลดตราประทับ</p>
                                    <small>รองรับไฟล์ PNG, JPEG, GIF (ขนาดไม่เกิน 2MB)</small>
                                </div>
                            )}
                            <input
                                ref={stampInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/gif"
                                onChange={handleStampUpload}
                                style={{ display: 'none' }}
                            />
                            {uploading && <div className="uploading-text">กำลังอัพโหลด...</div>}
                        </div>
                    </div>

                    {/* Stamp Config per Document Type */}
                    <h3 className="section-title">ตั้งค่าตราประทับสำหรับแต่ละเอกสาร</h3>
                    {!hospitalStamp && (
                        <div className="alert alert-warning">
                            กรุณาอัพโหลดตราประทับก่อนตั้งค่า
                        </div>
                    )}
                    <div className="config-grid">
                        {documentTypes.map(docType => {
                            const config = getConfigForType(docType.id);
                            const isEditingStamp = editingStampConfig?.document_type === docType.id;

                            return (
                                <div key={`stamp-${docType.id}`} className="steam-card config-card">
                                    <div className="steam-card-header flex-between">
                                        <span>{docType.name}</span>
                                        {!isEditingStamp && hospitalStamp && (
                                            <button
                                                className="steam-btn steam-btn-secondary steam-btn-sm"
                                                onClick={() => handleEditStampConfig(docType.id)}
                                            >
                                                ตั้งค่าตราประทับ
                                            </button>
                                        )}
                                    </div>
                                    <div className="steam-card-body">
                                        {isEditingStamp ? (
                                            <div className="config-form">
                                                <div className="form-group">
                                                    <label className="checkbox-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={editingStampConfig.stamp_enabled}
                                                            onChange={(e) => setEditingStampConfig({
                                                                ...editingStampConfig,
                                                                stamp_enabled: e.target.checked
                                                            })}
                                                        />
                                                        <span>เปิดใช้ตราประทับในเอกสารนี้</span>
                                                    </label>
                                                </div>

                                                <div className="form-group">
                                                    <label className="form-label">ตำแหน่งตราประทับ</label>
                                                    <select
                                                        className="steam-input"
                                                        value={editingStampConfig.stamp_position}
                                                        onChange={(e) => setEditingStampConfig({
                                                            ...editingStampConfig,
                                                            stamp_position: e.target.value
                                                        })}
                                                    >
                                                        <option value="above">บนลายเซ็น</option>
                                                        <option value="below">ใต้ลายเซ็น</option>
                                                    </select>
                                                </div>

                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label className="form-label">ชดเชย X</label>
                                                        <input
                                                            type="number"
                                                            className="steam-input"
                                                            value={editingStampConfig.stamp_offset_x}
                                                            onChange={(e) => setEditingStampConfig({
                                                                ...editingStampConfig,
                                                                stamp_offset_x: e.target.value
                                                            })}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label className="form-label">ชดเชย Y</label>
                                                        <input
                                                            type="number"
                                                            className="steam-input"
                                                            value={editingStampConfig.stamp_offset_y}
                                                            onChange={(e) => setEditingStampConfig({
                                                                ...editingStampConfig,
                                                                stamp_offset_y: e.target.value
                                                            })}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label className="form-label">ความกว้าง</label>
                                                        <input
                                                            type="number"
                                                            className="steam-input"
                                                            value={editingStampConfig.stamp_width}
                                                            onChange={(e) => setEditingStampConfig({
                                                                ...editingStampConfig,
                                                                stamp_width: e.target.value
                                                            })}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label className="form-label">ความสูง</label>
                                                        <input
                                                            type="number"
                                                            className="steam-input"
                                                            value={editingStampConfig.stamp_height}
                                                            onChange={(e) => setEditingStampConfig({
                                                                ...editingStampConfig,
                                                                stamp_height: e.target.value
                                                            })}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-actions">
                                                    <button
                                                        className="steam-btn steam-btn-secondary"
                                                        onClick={() => setEditingStampConfig(null)}
                                                    >
                                                        ยกเลิก
                                                    </button>
                                                    <button
                                                        className="steam-btn steam-btn-primary"
                                                        onClick={handleSaveStampConfig}
                                                        disabled={saving}
                                                    >
                                                        {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="config-info">
                                                <div className="info-item">
                                                    <span className="label">สถานะ:</span>
                                                    <span className={`status-badge ${config.stamp_enabled ? 'active' : 'inactive'}`}>
                                                        {config.stamp_enabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                                                    </span>
                                                </div>
                                                {config.stamp_enabled && (
                                                    <>
                                                        <div className="info-item">
                                                            <span className="label">ตำแหน่ง:</span>
                                                            <span className="value">
                                                                {config.stamp_position === 'above' ? 'บนลายเซ็น' : 'ใต้ลายเซ็น'}
                                                            </span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">ชดเชย:</span>
                                                            <span className="value">X: {config.stamp_offset_x || 0}, Y: {config.stamp_offset_y || 0}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">ขนาด:</span>
                                                            <span className="value">{config.stamp_width || 80} x {config.stamp_height || 80} px</span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {activeTab === 'signatures' && (
                <div className="steam-card">
                    <div className="steam-card-header">
                        <span>ลายเซ็นทั้งหมดในระบบ</span>
                    </div>
                    <div className="steam-card-body">
                        {signatures.length === 0 ? (
                            <div className="empty-state">
                                <p>ยังไม่มีลายเซ็นในระบบ</p>
                                <small>ผู้ใช้สามารถอัพโหลดลายเซ็นได้ที่หน้าโปรไฟล์</small>
                            </div>
                        ) : (
                            <div className="signatures-grid">
                                {signatures.map(sig => (
                                    <div key={sig.id} className={`signature-item ${!sig.is_active ? 'inactive' : ''}`}>
                                        <div className="signature-image-container">
                                            <img
                                                src={`/api${sig.file_url}`}
                                                alt={`ลายเซ็น ${sig.owner_name}`}
                                            />
                                        </div>
                                        <div className="signature-info">
                                            <strong>{sig.owner_name}</strong>
                                            <small>รหัส: {sig.emp_code || '-'}</small>
                                            <span className={`status-badge ${sig.is_active ? 'active' : 'inactive'}`}>
                                                {sig.is_active ? 'ใช้งานอยู่' : 'ไม่ใช้งาน'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminSignatures;
